import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Fuel, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';
import { FuelEntrySection } from './FuelEntrySection';
import { NonFuelDetailsSection } from './cost/NonFuelDetailsSection';
import { DuplicateWarningBanner } from './cost/DuplicateWarningBanner';
import { useReadingTypoGuard } from './cost/ReadingTypoGuard';

const COST_TYPES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'service', label: 'Service' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'labour', label: 'Labour' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'license', label: 'License' },
  { value: 'other', label: 'Other' },
];

const AddCostDialog = ({ open, onOpenChange, vehicles, defaultVehicleId, onSuccess }) => {
  const [formData, setFormData] = useState({
    vehicle_id: defaultVehicleId || '',
    cost_type: '',
    amount: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    odometer: '',
    hours: '',
    liters: '',
    price_per_liter: '',
    supplier_id: '',
    vendor: '',
    invoice_number: '',
    order_number: '',
    discount: '',
    notes: '',
    service_interval_km: ''
  });
  const [saving, setSaving] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState({ is_duplicate: false, duplicates: [] });
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [lastFuelEntry, setLastFuelEntry] = useState(null);
  const [fuelEfficiency, setFuelEfficiency] = useState(null);
  const [vehicleAvgKmL, setVehicleAvgKmL] = useState(null);
  const [allFuelEntries, setAllFuelEntries] = useState([]);
  const [priceDiscrepancy, setPriceDiscrepancy] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [lastVehicleOdo, setLastVehicleOdo] = useState(null);
  const [allVehicleCosts, setAllVehicleCosts] = useState([]);
  const { guard: guardReading, dialog: readingTypoDialog } = useReadingTypoGuard();
  const [serviceConfirmed, setServiceConfirmed] = useState(false);
  const [nextServiceOdo, setNextServiceOdo] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [alsoReportIncident, setAlsoReportIncident] = useState(false);
  const [incidentType, setIncidentType] = useState('accident');
  const [incidentDriverId, setIncidentDriverId] = useState('');
  const [incidentDamage, setIncidentDamage] = useState('');
  const dupTimerRef = useRef(null);

  // Get the selected vehicle's usage unit
  const selectedVehicle = vehicles?.find(v => v.id === formData.vehicle_id);
  const usageUnit = selectedVehicle?.usage_unit || 'kilometer';
  const isHoursBased = usageUnit === 'hours';
  // Vehicles flagged as excluded from the fleet average (private/yard vehicles) do
  // not track km/L, so an odometer/hours reading is not required on cost entries.
  const isMeterExempt = selectedVehicle?.include_in_fleet_avg === false;

  // Fetch suppliers when dialog opens
  useEffect(() => {
    if (open) {
      fetchSuppliers();
      // Fetch drivers so the "also report incident" reveal has them ready
      api.getDrivers().then(setDrivers).catch(() => setDrivers([]));
    }
  }, [open]);

  // Pre-fill service interval from vehicle when switching to service type
  useEffect(() => {
    if (formData.cost_type === 'service') {
      const v = vehicles?.find(veh => veh.id === formData.vehicle_id);
      if (v) {
        const interval = isHoursBased ? (v.service_interval_hours || 500) : (v.service_interval_km || 10000);
        setFormData(prev => ({ ...prev, service_interval_km: String(interval) }));
      }
    } else {
      // Reset service confirmation when switching away from service
      setServiceConfirmed(false);
      setNextServiceOdo('');
    }
  }, [formData.cost_type, formData.vehicle_id, vehicles, isHoursBased]);

  // Auto-fill nextServiceOdo when current odometer or interval changes
  useEffect(() => {
    const isService = formData.cost_type === 'service';
    if (!isService) return;
    const currentReading = isHoursBased ? formData.hours : formData.odometer;
    const interval = formData.service_interval_km;
    if (currentReading && interval && !nextServiceOdo) {
      const next = parseFloat(currentReading) + parseFloat(interval);
      setNextServiceOdo(String(Math.round(next)));
    }
  }, [formData.cost_type, formData.odometer, formData.hours, formData.service_interval_km, isHoursBased, nextServiceOdo]);

  // Robust km (or hours) per day from the backend — same maths used to save the next service date
  const [avgKmPerDay, setAvgKmPerDay] = useState(null);
  useEffect(() => {
    if (!formData.vehicle_id) { setAvgKmPerDay(null); return; }
    api.getUsageRate(formData.vehicle_id)
      .then((r) => setAvgKmPerDay(r?.per_day || null))
      .catch(() => setAvgKmPerDay(null));
  }, [formData.vehicle_id]);

  // Compute expected next service date based on usage history
  const expectedNextServiceDate = useMemo(() => {
    if (!nextServiceOdo || !avgKmPerDay || avgKmPerDay <= 0) return null;
    const currentReading = parseFloat(isHoursBased ? formData.hours : formData.odometer);
    const nextReading = parseFloat(nextServiceOdo);
    if (!currentReading || !nextReading || nextReading <= currentReading) return null;
    const usageRemaining = nextReading - currentReading;
    const daysUntilService = usageRemaining / avgKmPerDay;
    const baseDate = formData.date ? parseISO(formData.date) : new Date();
    return addDays(baseDate, Math.round(daysUntilService));
  }, [nextServiceOdo, avgKmPerDay, formData.odometer, formData.hours, formData.date, isHoursBased]);

  const fetchSuppliers = async () => {
    try {
      const data = await api.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  };

  // Real-time duplicate checking (debounced)
  const runDuplicateCheck = useCallback(async (vehicleId, invoiceNumber, date, amount) => {
    // Need at least invoice_number OR (vehicle_id + date + amount)
    const hasInvoice = invoiceNumber && invoiceNumber.trim().length >= 2;
    const hasDateAmount = vehicleId && date && amount && parseFloat(amount) > 0;
    if (!hasInvoice && !hasDateAmount) {
      setDuplicateCheck({ is_duplicate: false, duplicates: [] });
      return;
    }
    setCheckingDuplicate(true);
    try {
      const result = await api.checkDuplicate({
        vehicle_id: vehicleId || null,
        invoice_number: invoiceNumber?.trim() || null,
        date: date || null,
        amount: amount ? parseFloat(amount) : null,
      });
      setDuplicateCheck(result);
      setDuplicateOverride(false);
    } catch {
      setDuplicateCheck({ is_duplicate: false, duplicates: [] });
    } finally {
      setCheckingDuplicate(false);
    }
  }, []);

  useEffect(() => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    dupTimerRef.current = setTimeout(() => {
      runDuplicateCheck(formData.vehicle_id, formData.invoice_number, formData.date, formData.amount);
    }, 500);
    return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
  }, [formData.vehicle_id, formData.invoice_number, formData.date, formData.amount, runDuplicateCheck]);

  // Fetch last odometer/hours AND all costs for selected vehicle (used for service history calc)
  useEffect(() => {
    const fetchVehicleHistory = async () => {
      if (!formData.vehicle_id) {
        setLastVehicleOdo(null);
        setAllVehicleCosts([]);
        return;
      }
      try {
        const costs = await api.getCosts({ vehicle_id: formData.vehicle_id });
        if (costs.length > 0) {
          const sorted = [...costs].sort((a, b) => new Date(b.date) - new Date(a.date));
          const withOdo = sorted.find(c => c.odometer || c.hours);
          setLastVehicleOdo(withOdo || null);
          setAllVehicleCosts(sorted);
        } else {
          setLastVehicleOdo(null);
          setAllVehicleCosts([]);
        }
      } catch {
        setLastVehicleOdo(null);
        setAllVehicleCosts([]);
      }
    };
    fetchVehicleHistory();
  }, [formData.vehicle_id]);

  // Auto-derive price_per_liter from liters + amount for fuel entries.
  // Simplified: only Liters and Total are user inputs; price/L is calculated.
  useEffect(() => {
    if (formData.cost_type === 'fuel' && formData.liters && formData.amount) {
      const liters = parseFloat(formData.liters);
      const totalAmount = parseFloat(formData.amount);
      if (liters > 0 && totalAmount > 0) {
        const derived = (totalAmount / liters).toFixed(3);
        setFormData(prev => (prev.price_per_liter === derived ? prev : { ...prev, price_per_liter: derived }));
      }
    }
    setPriceDiscrepancy(null);
  }, [formData.liters, formData.amount, formData.cost_type]);

  // Auto-suggest a meter reading (hours for forklifts, odometer for others)
  // when the operator selects a vehicle + date and the meter field is still
  // blank. The suggestion is fetched from `/api/costs/suggest-usage`, which
  // interpolates between the nearest earlier and later entries. Never
  // overwrites a value the user has already typed.
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!formData.vehicle_id || !formData.date) { setSuggestion(null); return; }
      const empty = isHoursBased ? !formData.hours : !formData.odometer;
      if (!empty) { setSuggestion(null); return; }
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await import('axios').then(m => m.default.get(
          `${API}/api/costs/suggest-usage?vehicle_id=${formData.vehicle_id}&date=${formData.date}`
        ));
        if (cancelled) return;
        const val = isHoursBased ? r.data?.suggested_hours : r.data?.suggested_odometer;
        if (val != null && r.data?.source !== 'none') {
          setSuggestion({ value: val, source: r.data.source, meterType: isHoursBased ? 'hours' : 'km' });
        } else {
          setSuggestion(null);
        }
      } catch {
        if (!cancelled) setSuggestion(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [formData.vehicle_id, formData.date, formData.cost_type, isHoursBased, formData.hours, formData.odometer]);

  const applySuggestion = () => {
    if (!suggestion) return;
    if (isHoursBased) setFormData(prev => ({ ...prev, hours: String(suggestion.value) }));
    else setFormData(prev => ({ ...prev, odometer: String(suggestion.value) }));
  };

  // Fetch all fuel entries for the selected vehicle to calculate average km/L
  useEffect(() => {
    const fetchFuelEntries = async () => {
      if (formData.vehicle_id && formData.cost_type === 'fuel') {
        try {
          const costs = await api.getCosts({ vehicle_id: formData.vehicle_id, cost_type: 'fuel' });
          if (costs.length > 0) {
            // Sort by date and odometer to get the last entry
            const sorted = costs.sort((a, b) => {
              if (a.date !== b.date) return new Date(b.date) - new Date(a.date);
              return (b.odometer || 0) - (a.odometer || 0);
            });
            setLastFuelEntry(sorted[0]);
            setAllFuelEntries(sorted);
            
            // Calculate average km/L from historical data
            let totalKm = 0;
            let totalLiters = 0;
            for (let i = 0; i < sorted.length - 1; i++) {
              const current = sorted[i];
              const previous = sorted[i + 1];
              if (current.odometer && previous.odometer && current.liters) {
                const kmDriven = current.odometer - previous.odometer;
                if (kmDriven > 0) {
                  totalKm += kmDriven;
                  totalLiters += current.liters;
                }
              }
            }
            if (totalKm > 0 && totalLiters > 0) {
              setVehicleAvgKmL(totalKm / totalLiters);
            } else {
              setVehicleAvgKmL(null);
            }
          } else {
            setLastFuelEntry(null);
            setAllFuelEntries([]);
            setVehicleAvgKmL(null);
          }
        } catch (error) {
          setLastFuelEntry(null);
          setAllFuelEntries([]);
          setVehicleAvgKmL(null);
        }
      }
    };
    fetchFuelEntries();
  }, [formData.vehicle_id, formData.cost_type]);

  // Calculate fuel efficiency when odometer and liters are entered
  useEffect(() => {
    if (lastFuelEntry && formData.odometer && formData.liters && lastFuelEntry.odometer) {
      const kmDriven = parseFloat(formData.odometer) - lastFuelEntry.odometer;
      const litersUsed = parseFloat(formData.liters);
      if (kmDriven > 0 && litersUsed > 0) {
        const lPer100km = (litersUsed / kmDriven) * 100;
        const kmPerLiter = kmDriven / litersUsed;
        
        // Check for 10% variation from vehicle average
        let variationPercent = null;
        let hasVariation = false;
        if (vehicleAvgKmL && vehicleAvgKmL > 0) {
          variationPercent = ((kmPerLiter - vehicleAvgKmL) / vehicleAvgKmL) * 100;
          hasVariation = Math.abs(variationPercent) >= 10;
        }
        
        setFuelEfficiency({
          lPer100km: lPer100km.toFixed(2),
          kmPerLiter: kmPerLiter.toFixed(2),
          kmDriven: kmDriven.toFixed(0),
          litersUsed: litersUsed.toFixed(1),
          isAbnormal: lPer100km > 15 || lPer100km < 5, // Flag extreme consumption
          vehicleAvgKmL: vehicleAvgKmL ? vehicleAvgKmL.toFixed(2) : null,
          variationPercent: variationPercent ? variationPercent.toFixed(1) : null,
          hasVariation: hasVariation
        });
      } else {
        setFuelEfficiency(null);
      }
    } else {
      setFuelEfficiency(null);
    }
  }, [formData.odometer, formData.liters, lastFuelEntry, vehicleAvgKmL]);

  // NO auto-calculation - all values from invoice
  // Price verification happens in the priceDiscrepancy useEffect above

  const resetForm = () => {
    setFormData({
      vehicle_id: defaultVehicleId || '',
      cost_type: '',
      amount: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      odometer: '',
      hours: '',
      liters: '',
      price_per_liter: '',
      supplier_id: '',
      vendor: '',
      invoice_number: '',
      order_number: '',
      discount: '',
      notes: '',
      service_interval_km: ''
    });
    setLastFuelEntry(null);
    setFuelEfficiency(null);
    setPriceDiscrepancy(null);
    setDuplicateCheck({ is_duplicate: false, duplicates: [] });
    setDuplicateOverride(false);
    setServiceConfirmed(false);
    setNextServiceOdo('');
    setAlsoReportIncident(false);
    setIncidentType('accident');
    setIncidentDriverId('');
    setIncidentDamage('');
  };

  // Optimized field update handler
  const updateField = (field, value) => {
    setFormData(prev => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };

  const buildCostPayload = () => {
    const isService = formData.cost_type === 'service';
    // If user confirmed a custom next-service odo, recompute the interval from it
    let intervalToSend = formData.service_interval_km ? parseInt(formData.service_interval_km) : null;
    if (isService && nextServiceOdo) {
      const currentReading = parseFloat(isHoursBased ? formData.hours : formData.odometer);
      const nextReading = parseFloat(nextServiceOdo);
      if (currentReading && nextReading > currentReading) {
        intervalToSend = Math.round(nextReading - currentReading);
      }
    }
    return {
      vehicle_id: formData.vehicle_id,
      cost_type: formData.cost_type,
      amount: parseFloat(formData.amount),
      description: formData.description || null,
      date: formData.date,
      odometer: formData.odometer ? parseFloat(formData.odometer) : null,
      hours: formData.hours ? parseFloat(formData.hours) : null,
      liters: formData.liters ? parseFloat(formData.liters) : null,
      price_per_liter: formData.price_per_liter ? parseFloat(formData.price_per_liter) : null,
      supplier_id: formData.supplier_id || null,
      vendor: formData.vendor || null,
      invoice_number: formData.invoice_number || null,
      order_number: formData.order_number || null,
      discount: formData.discount ? parseFloat(formData.discount) : null,
      notes: formData.notes || null,
      service_interval_km: !isHoursBased ? intervalToSend : null,
      service_interval_hours: isHoursBased ? intervalToSend : null,
      // Backend projects the date from the same robust usage rate shown in the dialog.
      next_service_expected_date: null,
      next_service_km: isService && !isHoursBased && nextServiceOdo ? parseFloat(nextServiceOdo) : null,
      next_service_hours: isService && isHoursBased && nextServiceOdo ? parseFloat(nextServiceOdo) : null,
    };
  };

  const submitCost = async (payload, force = false) => {
    setSaving(true);
    try {
      await api.createCost(payload, force);
      // Optional: also create an incident linked to this expense.
      // Driver validation happened before we reached this point (in handleSubmit).
      if (alsoReportIncident && incidentDriverId) {
        try {
          await api.createIncident({
            vehicle_id: payload.vehicle_id,
            date: payload.date,
            incident_date: payload.date,
            incident_type: incidentType,
            description: incidentDamage || payload.description || 'Reported from expense entry',
            damage_description: incidentDamage || null,
            driver_id: incidentDriverId,
            damage_estimate: payload.amount,
            status: 'open',
          });
          toast.success('Cost entry added — incident report created');
        } catch (incErr) {
          toast.warning('Cost saved, but incident report failed. Please add it manually on Incidents page.');
          console.error('Incident linked-create failed', incErr);
        }
      } else {
        toast.success('Cost entry added');
      }
      onOpenChange(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        toast.error('Duplicate entry blocked. An entry with the same invoice number or date and amount already exists.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to add cost');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic required fields
    if (!formData.vehicle_id || !formData.cost_type || !formData.amount || !formData.date) {
      toast.error('Please fill in all required fields: Vehicle, Cost Type, Amount, and Date');
      return;
    }

    // Usage reading required (either odometer or hours based on vehicle type),
    // unless the vehicle is excluded from the fleet average.
    const hasUsageReading = isHoursBased ? formData.hours : formData.odometer;
    if (!hasUsageReading && !isMeterExempt) {
      toast.error(`${isHoursBased ? 'Hours' : 'Odometer'} reading is required`);
      return;
    }

    // Additional validation for fuel entries
    if (formData.cost_type === 'fuel') {
      if (!formData.liters) {
        toast.error('Liters is required for fuel entries');
        return;
      }
      if (!formData.supplier_id && !formData.vendor) {
        toast.error('Supplier is required for fuel entries');
        return;
      }
    }

    // Service confirmation: require user to tick "this is a service" checkbox
    // and confirm/edit the next service odometer
    const isServiceEntry = formData.cost_type === 'service';
    if (isServiceEntry) {
      if (!serviceConfirmed) {
        toast.error('Please confirm that this is a service entry');
        return;
      }
      if (!nextServiceOdo || parseFloat(nextServiceOdo) <= 0) {
        toast.error(`Please confirm the next ${isHoursBased ? 'hours' : 'odometer'} reading for the next service`);
        return;
      }
      const currentReading = parseFloat(isHoursBased ? formData.hours : formData.odometer);
      if (parseFloat(nextServiceOdo) <= currentReading) {
        toast.error(`Next service ${isHoursBased ? 'hours' : 'odometer'} must be greater than current reading`);
        return;
      }
    }

    // Block if duplicate detected and not overridden
    if (duplicateCheck.is_duplicate && !duplicateOverride) {
      toast.error('Duplicate detected — check the override box to proceed.');
      return;
    }

    // If the "Also report as incident" checkbox is on, require driver BEFORE we save anything.
    if (alsoReportIncident && !incidentDriverId) {
      toast.error('Please select the driver at fault before saving.');
      return;
    }

    const payload = buildCostPayload();
    const force = duplicateCheck.is_duplicate && duplicateOverride;
    await guardReading(
      { vehicleId: formData.vehicle_id, date: formData.date, reading: isHoursBased ? formData.hours : formData.odometer },
      () => submitCost(payload, force),
    );
  };

  const isFuelEntry = formData.cost_type === 'fuel';

  // Filter suppliers by type for the current cost type
  const getRelevantSuppliers = () => {
    if (!formData.cost_type) return suppliers;
    const typeMap = {
      'fuel': 'fuel',
      'tyres': 'tyres',
      'service': 'service',
      'maintenance': 'service',
      'labour': 'service',
      'insurance': 'insurance'
    };
    const supplierType = typeMap[formData.cost_type];
    if (!supplierType) return suppliers;
    // Show matching type suppliers first, then general, then others
    return suppliers.sort((a, b) => {
      if (a.supplier_type === supplierType && b.supplier_type !== supplierType) return -1;
      if (b.supplier_type === supplierType && a.supplier_type !== supplierType) return 1;
      if (a.supplier_type === 'general' && b.supplier_type !== 'general') return -1;
      if (b.supplier_type === 'general' && a.supplier_type !== 'general') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black flex items-center gap-2">
            {isFuelEntry && <Fuel className="w-5 h-5 text-orange-500" />}
            {(formData.cost_type === 'service') && <Wrench className="w-5 h-5 text-blue-700" />}
            Add Cost Entry
          </DialogTitle>
          <DialogDescription>
            Record a new expense for your vehicle
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Vehicle *</Label>
            <Select 
              value={formData.vehicle_id} 
              onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
            >
              <SelectTrigger data-testid="cost-vehicle-select">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                {vehicles?.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name} ({v.registration})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost Type *</Label>
              <Select 
                value={formData.cost_type} 
                onValueChange={(value) => setFormData({ ...formData, cost_type: value })}
              >
                <SelectTrigger data-testid="cost-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {COST_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Amount field only for non-fuel entries - fuel has it in invoice section */}
            {!isFuelEntry && (
              <div className="space-y-2">
                <Label>Amount (R) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => updateField('amount', e.target.value)}
                  placeholder="0.00"
                  className="font-data"
                  data-testid="cost-amount-input"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Date *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => updateField('date', e.target.value)}
              className="font-data"
              data-testid="cost-date-input"
            />
          </div>

          {/* Fuel-specific fields */}
          {isFuelEntry && (
            <FuelEntrySection
              formData={formData}
              updateField={updateField}
              setFormData={setFormData}
              isHoursBased={isHoursBased}
              isMeterExempt={isMeterExempt}
              lastFuelEntry={lastFuelEntry}
              lastVehicleOdo={lastVehicleOdo}
              fuelEfficiency={fuelEfficiency}
              suppliers={suppliers}
              getRelevantSuppliers={getRelevantSuppliers}
              suggestion={suggestion}
              onApplySuggestion={applySuggestion}
            />
          )}

          {/* Non-fuel: Usage reading is REQUIRED, vendor and invoice optional */}
          {!isFuelEntry && formData.cost_type && (
            <NonFuelDetailsSection
              formData={formData}
              updateField={updateField}
              setFormData={setFormData}
              isHoursBased={isHoursBased}
              isMeterExempt={isMeterExempt}
              lastVehicleOdo={lastVehicleOdo}
              suppliers={suppliers}
              getRelevantSuppliers={getRelevantSuppliers}
              serviceConfirmed={serviceConfirmed}
              setServiceConfirmed={setServiceConfirmed}
              nextServiceOdo={nextServiceOdo}
              setNextServiceOdo={setNextServiceOdo}
              expectedNextServiceDate={expectedNextServiceDate}
              avgKmPerDay={avgKmPerDay}
              allVehicleCosts={allVehicleCosts}
              selectedVehicle={selectedVehicle}
            />
          )}

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder={isFuelEntry ? "e.g., Full tank fill-up" : "e.g., Oil change, brake pads"}
              data-testid="cost-description-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
              data-testid="cost-notes-input"
            />
          </div>

          {/* Also report as incident */}
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={alsoReportIncident}
                onChange={(e) => setAlsoReportIncident(e.target.checked)}
                className="mt-1 h-4 w-4 accent-amber-600"
                data-testid="cost-also-incident-checkbox"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-amber-900">Also report this as an incident</span>
                <p className="text-xs text-amber-700">Automatically logs an incident report tied to this vehicle & date (breakdown, damage, etc).</p>
              </div>
            </label>
            {alsoReportIncident && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Incident Type *</Label>
                  <Select value={incidentType} onValueChange={setIncidentType}>
                    <SelectTrigger data-testid="cost-incident-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accident">Accident</SelectItem>
                      <SelectItem value="breakdown">Breakdown</SelectItem>
                      <SelectItem value="theft">Theft</SelectItem>
                      <SelectItem value="vandalism">Vandalism</SelectItem>
                      <SelectItem value="near_miss">Near Miss</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Driver at fault *</Label>
                  {drivers.length === 0 ? (
                    <p className="text-xs text-red-600">No drivers on record — <a href="/drivers" className="underline">add one</a> before saving.</p>
                  ) : (
                    <Select value={incidentDriverId} onValueChange={setIncidentDriverId}>
                      <SelectTrigger data-testid="cost-incident-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                      <SelectContent>
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}{d.license_number ? ` — ${d.license_number}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Damage description (optional)</Label>
                  <Textarea
                    value={incidentDamage}
                    onChange={(e) => setIncidentDamage(e.target.value)}
                    placeholder="What was damaged and how..."
                    rows={2}
                    data-testid="cost-incident-damage-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Duplicate Warning Banner */}
          <DuplicateWarningBanner
            duplicateCheck={duplicateCheck}
            duplicateOverride={duplicateOverride}
            setDuplicateOverride={setDuplicateOverride}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className={duplicateCheck.is_duplicate && !duplicateOverride ? "bg-gray-400 cursor-not-allowed" : duplicateCheck.is_duplicate && duplicateOverride ? "bg-amber-600 hover:bg-amber-700" : ""}
              disabled={
                saving
                || (duplicateCheck.is_duplicate && !duplicateOverride)
                || ((formData.cost_type === 'service') && !serviceConfirmed)
              }
              data-testid="save-cost-btn"
            >
              {saving
                ? 'Saving...'
                : duplicateCheck.is_duplicate && !duplicateOverride
                  ? 'Blocked — Duplicate'
                  : (formData.cost_type === 'service') && !serviceConfirmed
                    ? 'Confirm service first'
                    : duplicateCheck.is_duplicate && duplicateOverride
                      ? 'Override & Add'
                      : 'Add Cost'}
            </Button>
          </div>
        </form>
      </DialogContent>
      {readingTypoDialog}
    </Dialog>
  );
};

export default AddCostDialog;
